import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

const brandCard = {
    topBarColor: 'from-brand-500 to-brand-700',
    border: 'border-brand-100',
    bg: 'hover:bg-brand-50/50',
}

// Siglas, ligações e logótipos ficam no código (são nomes próprios e URLs);
// o nome por extenso de cada entidade vem do catálogo (infoUteis.orgs.<key>).
const migrantOrgs = [
    { key: 'aima', shortName: 'AIMA', link: 'https://aima.gov.pt/pt', logo: '/assets/images/logos-orgs/aima_logo.png' },
    { key: 'cepac', shortName: 'CEPAC', link: 'https://www.cepac.pt', logo: '/assets/images/logos-orgs/cepac_logo.png' },
    { key: 'cpr', shortName: 'CPR', link: 'https://cpr.pt/', logo: '/assets/images/logos-orgs/cpr_logo.png' },
    { key: 'crescer', shortName: 'CRESCER', link: 'https://crescer.org/', logo: '/assets/images/logos-orgs/crescer_logo.png' },
    { key: 'jrs', shortName: 'JRS Portugal', link: 'https://www.jrsportugal.pt/', logo: '/assets/images/logos-orgs/jrs_logo.png' },
    { key: 'lisbonProject', shortName: 'Lisbon Project', link: 'https://www.lisbonproject.org/our-work', logo: '/assets/images/logos-orgs/lisbon_project_logo.png' },
    { key: 'oim', shortName: 'OIM', link: 'https://portugal.iom.int/pt-pt/oim-em-portugal', logo: '/assets/images/logos-orgs/oim_logo.png' },
    { key: 'renovar', shortName: 'Renovar a Mouraria', link: 'https://renovaramouraria.pt/pt/', logo: '/assets/images/logos-orgs/renovar_mouraria_logo.png' },
]

const touristOrgs = [
    { key: 'visitPortugal', shortName: 'Visit Portugal', link: 'https://www.visitportugal.com/pt-pt', logo: '/assets/images/logos-orgs/visit_portugal_logo.png' },
]

function OrgCard({ org, index }) {
    const { t } = useTranslation()
    return (
        <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            className={`bg-white rounded-2xl shadow-xs border ${brandCard.border} overflow-hidden flex flex-col group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${brandCard.bg}`}
        >
            {/* Top colour bar */}
            <div className={`h-1.5 bg-gradient-to-r ${brandCard.topBarColor} w-full`} />

            {/* Logo — tall container so wide and tall logos all appear large */}
            <div className="w-full h-36 border-b border-slate-100 bg-white px-4 py-3">
                <img
                    src={org.logo}
                    alt={t('infoUteis.logoAlt', { org: org.shortName })}
                    className="w-full h-full object-contain"
                />
            </div>

            {/* Text + button */}
            <div className="flex flex-col flex-1 px-6 py-5">
                <h2 className="text-base font-bold text-slate-900 mb-1" translate="no">{org.shortName}</h2>
                <p className="text-slate-500 text-sm mb-5 flex-1 leading-relaxed">{t(`infoUteis.orgs.${org.key}`)}</p>
                <a
                    href={org.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('infoUteis.visitAria', { org: org.shortName })}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r ${brandCard.topBarColor} text-white font-semibold text-sm shadow-md hover:opacity-90 transition-opacity`}
                >
                    {t('infoUteis.visit')}
                    <ExternalLink size={14} aria-hidden="true" />
                </a>
            </div>
        </motion.div>
    )
}

export default function InformacoesUteis() {
    const { t } = useTranslation()

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head>.
                Ficam aqui, na raiz da página, e não dentro do OrgCard: lá dentro
                eram repetidas uma vez por cartão (9 <title> içados). */}
            <title>{t('infoUteis.metaTitle')}</title>
            <meta name="description" content={t('infoUteis.metaDescription')} />
            <link rel="canonical" href={pageUrl('informacoes-uteis')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('infoUteis.title')}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Migrantes Section */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <p className="text-lg text-slate-700 leading-relaxed mb-8">
                            {t('infoUteis.migrantesIntro')}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {migrantOrgs.map((org, i) => (
                                <OrgCard key={org.key} org={org} index={i} />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Turistas Section */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <p className="text-lg text-slate-700 leading-relaxed mb-8">
                            {t('infoUteis.turistasIntro')}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {touristOrgs.map((org, i) => (
                                <OrgCard key={org.key} org={org} index={i} />
                            ))}
                        </div>
                        <div className="mt-6">
                            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                                <p className="text-slate-700 font-bold text-lg">
                                    {t('infoUteis.embassy')}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
